import React, { useState, useEffect, FormEvent } from 'react';
import { useParams } from 'react-router-dom';
import { useSnackbar } from '../contexts/SnackbarContext';
import { FormGroupModel } from '../dashboard/form-groups/create-form-group-dialog';
import { FormField, FormModel } from '../models/form.model';

const ThankYouScreen = () => (
    <div className="text-center py-12 flex flex-col justify-center items-center gap-10">
        <img src='/assets/cerebrum.png' className='w-64 h-64' alt='logo'></img>
        <h2 className="text-2xl font-bold mb-4">Köszönjük a kitöltést!</h2>
        <p className="text-gray-600">Az adatok rögzítésre kerültek.</p>
    </div>
);

const NotFound = () => (
    <div className="text-center py-12 flex flex-col justify-center items-center gap-10">
        <img src='/assets/cerebrum.png' className='w-64 h-64' alt='logo'></img>
        <h2 className="text-2xl font-bold mb-4">Úgy tűnik eltévedtél...</h2>
        <p className="text-gray-600">Ez a kérdőív nem létezik.</p>
    </div>
);

function FormSubmit() {
    const { shareId } = useParams();
    const { showSnackbar } = useSnackbar();

    const [form, setForm] = useState<FormModel | null>(null);

    const [formGroup, setFormGroup] = useState<FormGroupModel | null>(null);
    const [formGroupIndex, setFormGroupIndex] = useState<number>(0);

    const [values, setValues] = useState<Record<string, any>>({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [submissionId, setSubmissionId] = useState("");

    useEffect(() => {
        fetchForm();
        setSubmissionId(makeid(8));
    }, []);

    const fetchForm = async () => {
        const formResponse = await fetch(`/api/forms/share/${shareId}`);
        const formGroupResponse = await fetch(`/api/form-group/share/${shareId}`);
        try {
            if (!formResponse.ok) {
                if (!formGroupResponse.ok) {
                    throw new Error('Form not found');
                }
                const data = await formGroupResponse.json();
                setFormGroup(data);
                setFormGroupIndex(0);

                if (data && data.forms.length > 0) {
                    loadForm(data.forms[formGroupIndex]);
                }
                return;
            }
            const data = await formResponse.json();
            loadForm(data);
        } catch (err) {
            setError(true);
        } finally {
            setLoading(false);
        }
    };

    const loadForm = (data: FormModel) => {
        setForm(data);
        initializeValues(data.fields);
    }

    const initializeValues = (fields: FormField[]) => {
        const initialValues: Record<string, any> = {};
        fields.forEach(field => {
            if (field.type !== 'info' && field.type !== 'image-info') {
                initialValues[field.id] = field.type === 'checkbox' ? false : '';
            }
        });
        setValues(initialValues);
    };

    const handleChange = (fieldId: string, value: any) => {
        setValues(prev => ({ ...prev, [fieldId]: value }));
    };

    const renderTextWithNewlines = (text: string) => {
        return text.split('\n').map((line, index) => (
            <React.Fragment key={index}>
                {line}
                <br />
            </React.Fragment>
        ));
    };

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();
        try {
            if (form === null) {
                return;
            }
            let formId = form._id;
            const submission = {
                formId,
                submissionId,
                values: Object.entries(values).map(([fieldId, value]) => ({
                    fieldId,
                    value
                }))
            };

            const response = await fetch('/api/submissions', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(submission)
            });

            if (!response.ok) throw new Error('Submission failed');
            if (!formGroup) {
                setIsSubmitted(true);
                initializeValues(form?.fields || []);
            } else {
                if (formGroup.forms[formGroupIndex + 1]) {
                    loadForm(formGroup.forms[formGroupIndex + 1]);
                    setFormGroupIndex(formGroupIndex + 1);
                } else {
                    setIsSubmitted(true);
                    initializeValues(form?.fields || []);
                }
            }
        } catch (err) {
            showSnackbar('Failed to submit form', 'error');
        }
    };

    function makeid(length: number) {
        let result = '';
        const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
        const charactersLength = characters.length;
        let counter = 0;
        while (counter < length) {
          result += characters.charAt(Math.floor(Math.random() * charactersLength));
          counter += 1;
        }
        return result;
    }

    if (loading) return <div>Loading...</div>;
    if (error) return <NotFound />;
    if (!form) return <div>Form not found</div>;
    if (isSubmitted) return <ThankYouScreen />;

    return (
        <div className="max-w-4xl mx-auto p-6">
            <h1 className="text-2xl font-bold mb-4">{form.title}</h1>
            {form.description && <p className="mb-6 text-gray-600">{form.description}</p>}

            <form onSubmit={handleSubmit} className="space-y-6">
                {form.fields.map(field => (
                    <div key={field.id} className="space-y-2">
                        {field.type === 'info' ? (
                            <div
                                className={`p-4 bg-gray-50 rounded ${field.textStyle === 'bold' ? 'font-bold' :
                                    field.textStyle === 'italic' ? 'italic' : ''
                                    }`}
                            >
                                <span>{renderTextWithNewlines(field.content ?? '')}</span>
                            </div>
                        ) : (
                            <>
                                <label className="block font-medium">
                                    {
                                        field.type === 'image-info' && field.imageUrl ? (
                                            <div className="rounded-lg overflow-hidden">
                                                <img
                                                    src={field.imageUrl}
                                                    alt={field.label}
                                                    className="max-w-full max-h-96 object-contain mx-auto"
                                                />
                                            </div>
                                        ) : field.label
                                    }
                                    {field.required && <span className="text-red-500 ml-1">*</span>}
                                </label>

                                {field.type === 'text' && (
                                    <input
                                        type="text"
                                        className="w-full p-2 border rounded"
                                        value={values[field.id] || ''}
                                        onChange={e => handleChange(field.id, e.target.value)}
                                        placeholder={field.placeholder}
                                        required={field.required}
                                    />
                                )}

                                {field.type === 'textarea' && (
                                    <textarea
                                        className="w-full p-2 border rounded"
                                        value={values[field.id] || ''}
                                        onChange={e => handleChange(field.id, e.target.value)}
                                        placeholder={field.placeholder}
                                        required={field.required}
                                    />
                                )}

                                {field.type === 'number' && (
                                    <input
                                        type="number"
                                        className="w-full p-2 border rounded"
                                        value={values[field.id] || ''}
                                        onChange={e => handleChange(field.id, e.target.value)}
                                        placeholder={field.placeholder}
                                        required={field.required}
                                    />
                                )}

                                {field.type === 'email' && (
                                    <input
                                        type="email"
                                        className="w-full p-2 border rounded"
                                        value={values[field.id] || ''}
                                        onChange={e => handleChange(field.id, e.target.value)}
                                        placeholder={field.placeholder}
                                        required={field.required}
                                    />
                                )}

                                {field.type === 'checkbox' && (
                                    <label className="flex items-center space-x-2">
                                        <input
                                            type="checkbox"
                                            checked={values[field.id] || false}
                                            onChange={e => handleChange(field.id, e.target.checked)}
                                            required={field.required}
                                        />
                                        <span>{field.label}</span>
                                    </label>
                                )}

                                {field.type === 'select' && (
                                    <select
                                        className="w-full p-2 border rounded"
                                        value={values[field.id] || ''}
                                        onChange={e => handleChange(field.id, e.target.value)}
                                        required={field.required}
                                    >
                                        <option value="">Select an option</option>
                                        {field.options?.map(option => (
                                            <option key={option} value={option}>{option}</option>
                                        ))}
                                    </select>
                                )}

                                {field.type === 'radio' && (
                                    <div className="space-y-2">
                                        {field.options?.map(option => (
                                            <label key={option} className="flex items-center space-x-2">
                                                <input
                                                    type="radio"
                                                    name={field.id}
                                                    value={option}
                                                    checked={values[field.id] === option}
                                                    onChange={e => handleChange(field.id, e.target.value)}
                                                    required={field.required}
                                                />
                                                <span>{option}</span>
                                            </label>
                                        ))}
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                ))}

                <button
                    type="submit"
                    className="px-4 py-2 bg-orange-500 text-white rounded hover:bg-orange-600"
                >
                    {formGroup ? 'Submit & Next' : "Submit"}
                </button>
            </form>
        </div>
    );
};

export default FormSubmit;